import React, { useMemo, useState } from "react"
import { useSelector } from "react-redux"
import ReactApexChart from "react-apexcharts"

import { httpOk, GET } from "helpers/api"
import { formatMoney } from "helpers/helpers"
import AnalyticsFilterBar, { FilterField, FilterSelect, StandardFields } from "../parts/AnalyticsFilterBar"
import useAnalyticsFilters from "../parts/useAnalyticsFilters"
import useAnalyticsOptions from "../parts/useAnalyticsOptions"
import { buildPeriod, buildAnalyticsUrl } from "../parts/analyticsDates"

const cardStyle = {
	border: "1px solid var(--dark-border, #edf0f4)",
	borderRadius: "12px",
	boxShadow: "0 4px 14px rgba(15, 23, 42, 0.04)",
}

const mutedStyle = {
	color: "var(--dark-text-muted, #8791a1)",
	fontSize: "12px",
}

const COST_HINT = "Себестоимость — снимок, который касса записала в строку чека в момент продажи (по техкарте на тот момент); задним числом не пересчитывается"

const tabs = [
	{ id: "overview", label: "Обзор" },
	{ id: "branches", label: "По филиалам" },
]

function formatQty(value) {
	const number = Number(value || 0)
	return Number.isInteger(number) ? formatMoney(number, 0) : formatMoney(number, 2)
}

function fcBadgeStyle(fc, target) {
	const value = Number(fc || 0)
	const limit = Number(target || 40)
	if (value <= limit) return { background: "var(--dark-tint-success, #e7f8ef)", color: "var(--dark-fg-success, #1f9d57)" }
	if (value >= limit * 1.25) return { background: "var(--dark-tint-danger, #fdecec)", color: "var(--dark-fg-danger, #d23434)" }
	return { background: "var(--dark-tint-warning, #fdf3da)", color: "var(--dark-fg-warning, #c79100)" }
}

function fcBarColor(fc, target) {
	const value = Number(fc || 0)
	const limit = Number(target || 40)
	if (value <= limit) return "#1f9d57"
	if (value >= limit * 1.25) return "#e23b3b"
	return "#f0b429"
}

function FcBadge({ value, target }) {
	const style = fcBadgeStyle(value, target)
	return (
		<span style={{ ...style, borderRadius: "12px", padding: "3px 9px", fontWeight: 700, fontSize: "12px" }}>
			{Number(value || 0).toFixed(1)}%
		</span>
	)
}

function KpiCard({ title, value, sublabel, valueColor = "var(--dark-text-strong, #111827)", icon }) {
	return (
		<div className="card h-100" style={cardStyle}>
			<div className="card-body py-3 px-3">
				<div className="d-flex justify-content-between align-items-start">
					<div style={{ ...mutedStyle, textTransform: "uppercase", letterSpacing: "0.4px" }}>{title}</div>
					{icon && <i className={`uil ${icon} text-muted`} style={{ fontSize: "16px" }}></i>}
				</div>
				<h3 className="mb-0 mt-2" style={{ fontSize: "26px", fontWeight: 800, color: valueColor, letterSpacing: "-0.5px" }}>
					{value}
				</h3>
				{sublabel && <div style={{ ...mutedStyle, marginTop: "2px" }}>{sublabel}</div>}
			</div>
		</div>
	)
}

function ProblemTable({ products, target }) {
	const [onlyAbove, setOnlyAbove] = useState(true)
	const [visibleCount, setVisibleCount] = useState(15)

	const filtered = useMemo(
		() => (onlyAbove ? products.filter(item => item.above_norm) : products),
		[products, onlyAbove]
	)
	const visible = filtered.slice(0, visibleCount)
	const remaining = filtered.length - visible.length
	const headerThStyle = { border: "none", boxShadow: "none", background: "var(--dark-surface, #fff)", fontSize: "13px" }

	return (
		<div className="card mb-3" style={cardStyle}>
			<div className="card-body">
				<div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
					<div>
						<h5 className="mb-1 d-flex align-items-center gap-2">
							<i className="uil uil-chart-down"></i>
							Топ проблемных блюд
						</h5>
						<div style={mutedStyle}>Сколько денег теряется из-за превышения целевой нормы</div>
					</div>
					<button
						className="btn btn-sm d-inline-flex align-items-center gap-2"
						style={{
							borderRadius: "10px",
							border: onlyAbove ? "1px solid var(--bs-primary)" : "1px solid var(--dark-border, #eef0f4)",
							background: onlyAbove ? "rgba(var(--bs-primary-rgb), 0.08)" : "#fff",
							color: onlyAbove ? "var(--bs-primary)" : "var(--dark-text-muted, #5b6472)",
							fontWeight: 600,
						}}
						onClick={() => { setOnlyAbove(!onlyAbove); setVisibleCount(15) }}
					>
						<i className={`uil ${onlyAbove ? "uil-check-circle" : "uil-circle"}`}></i>
						Только блюда выше нормы
					</button>
				</div>

				<div className="table-responsive">
					<table className="table align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
						<thead>
							<tr style={{ border: "none" }}>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>#</th>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>Блюдо</th>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>Категория</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Продано</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Выручка</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle} title={COST_HINT}>Себестоимость <i className="uil uil-info-circle"></i></th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Food Cost %</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Потеря</th>
							</tr>
						</thead>
						<tbody>
							{visible.map((product, index) => {
								const borderBottom = index === visible.length - 1 ? "none" : "1px solid var(--dark-border, #eef2f6)"
								return (
									<tr key={product.product_id}>
										<td className="py-3 text-muted" style={{ borderBottom }}>{index + 1}</td>
										<td className="py-3" style={{ borderBottom, fontWeight: 600 }}>{product.product_name}</td>
										<td className="py-3 text-muted" style={{ borderBottom }}>{product.category_name}</td>
										<td className="py-3 text-end" style={{ borderBottom }}>{formatQty(product.quantity)}</td>
										<td className="py-3 text-end" style={{ borderBottom }}>{product.revenue_short}</td>
										<td className="py-3 text-end" style={{ borderBottom }}>{product.cost_short}</td>
										<td className="py-3 text-end" style={{ borderBottom }}><FcBadge value={product.food_cost_percent} target={target} /></td>
										<td className="py-3 text-end" style={{ borderBottom, color: Number(product.loss) < 0 ? "#d23434" : "var(--dark-fg-success, #1f9d57)", fontWeight: 600 }}>
											{product.loss_short}
										</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>

				{remaining > 0 && (
					<div className="text-center pt-3">
						<button className="btn btn-sm btn-link text-decoration-none" onClick={() => setVisibleCount(visibleCount + 20)}>
							Показать ещё ({remaining}) →
						</button>
					</div>
				)}
			</div>
		</div>
	)
}

function EffectiveTable({ products, target }) {
	const headerThStyle = { border: "none", boxShadow: "none", background: "var(--dark-surface, #fff)", fontSize: "13px" }
	return (
		<div className="card" style={cardStyle}>
			<div className="card-body">
				<h5 className="mb-1 d-flex align-items-center gap-2">
					<i className="uil uil-chart-growth"></i>
					Топ эффективных блюд
				</h5>
				<div style={mutedStyle} className="mb-3">Низкий food cost и хорошая прибыль</div>
				<div className="table-responsive">
					<table className="table align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
						<thead>
							<tr style={{ border: "none" }}>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>Блюдо</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Выручка</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Прибыль</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>FC %</th>
							</tr>
						</thead>
						<tbody>
							{products.length === 0 && (
								<tr><td colSpan="4" className="text-muted text-center py-4">Нет данных</td></tr>
							)}
							{products.map((product, index) => {
								const borderBottom = index === products.length - 1 ? "none" : "1px solid var(--dark-border, #eef2f6)"
								return (
									<tr key={product.product_id}>
										<td className="py-3" style={{ borderBottom, fontWeight: 600 }}>{product.product_name}</td>
										<td className="py-3 text-end" style={{ borderBottom }}>{product.revenue_short}</td>
										<td className="py-3 text-end" style={{ borderBottom, color: "var(--dark-fg-success, #1f9d57)", fontWeight: 600 }}>{product.profit_short}</td>
										<td className="py-3 text-end" style={{ borderBottom }}><FcBadge value={product.food_cost_percent} target={target} /></td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	)
}

function BranchesTable({ branches, target }) {
	const headerThStyle = { border: "none", boxShadow: "none", background: "var(--dark-surface, #fff)", fontSize: "13px" }
	return (
		<div className="card mb-3" style={cardStyle}>
			<div className="card-body">
				<div className="table-responsive">
					<table className="table align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
						<thead>
							<tr style={{ border: "none" }}>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>#</th>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>Филиал</th>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>Менеджер</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Выручка</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Себестоимость</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Food Cost % ▾</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>vs цель</th>
							</tr>
						</thead>
						<tbody>
							{branches.length === 0 && (
								<tr><td colSpan="7" className="text-muted text-center py-4">Нет данных</td></tr>
							)}
							{branches.map((branch, index) => {
								const borderBottom = index === branches.length - 1 ? "none" : "1px solid var(--dark-border, #eef2f6)"
								const diff = Number(branch.vs_target || 0)
								return (
									<tr key={branch.pos_id}>
										<td className="py-3 text-muted" style={{ borderBottom }}>{index + 1}</td>
										<td className="py-3" style={{ borderBottom, fontWeight: 600 }}>{branch.pos_name}</td>
										<td className="py-3 text-muted" style={{ borderBottom }}>{branch.manager_name || "—"}</td>
										<td className="py-3 text-end" style={{ borderBottom }}>{branch.revenue_short}</td>
										<td className="py-3 text-end" style={{ borderBottom }}>{branch.cost_short}</td>
										<td className="py-3 text-end" style={{ borderBottom }}><FcBadge value={branch.food_cost_percent} target={target} /></td>
										<td className="py-3 text-end" style={{ borderBottom, color: diff > 0 ? "#d23434" : "var(--dark-fg-success, #1f9d57)", fontWeight: 600 }}>
											{diff > 0 ? "+" : ""}{diff.toFixed(1)} п.п.
										</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	)
}

function BranchesChart({ branches, target }) {
	const series = [{ name: "Food Cost %", data: branches.map(b => Number(b.food_cost_percent || 0)) }]
	const options = {
		chart: { type: "bar", toolbar: { show: false } },
		plotOptions: { bar: { horizontal: true, borderRadius: 4, distributed: true, barHeight: "70%" } },
		colors: branches.map(b => fcBarColor(b.food_cost_percent, target)),
		dataLabels: { enabled: false },
		legend: { show: false },
		grid: { borderColor: "#eef2f6" },
		xaxis: {
			categories: branches.map(b => b.pos_name),
			labels: { formatter: (value) => `${Math.round(value)}%` },
		},
		tooltip: { y: { formatter: (value) => `${Number(value).toFixed(1)}%` } },
		annotations: {
			xaxis: [{
				x: Number(target || 40),
				borderColor: "#9aa5b1",
				strokeDashArray: 4,
				label: { text: `цель ${Number(target || 40).toFixed(0)}%`, style: { color: "#5b6472", background: "#f1f4f8" } },
			}],
		},
	}
	return (
		<div className="card mb-3" style={cardStyle}>
			<div className="card-body">
				<h5 className="mb-3">Food Cost % по филиалам</h5>
				<ReactApexChart options={options} series={series} type="bar" height={Math.max(260, branches.length * 34)} />
			</div>
		</div>
	)
}

function BestWorstGap({ branches }) {
	if (branches.length < 2) return null
	const sorted = [...branches].sort((a, b) => Number(a.food_cost_percent) - Number(b.food_cost_percent))
	const best = sorted[0]
	const worst = sorted[sorted.length - 1]
	const gap = Number(worst.food_cost_percent) - Number(best.food_cost_percent)
	const savings = Number(worst.revenue || 0) * gap / 100

	return (
		<div className="card" style={cardStyle}>
			<div className="card-body">
				<h5 className="mb-2 d-flex align-items-center gap-2">
					<i className="uil uil-exchange"></i>
					Разрыв между лучшим и худшим филиалом
				</h5>
				<div style={{ fontSize: "34px", fontWeight: 800, color: "var(--dark-text-strong, #111827)", letterSpacing: "-0.5px" }}>
					{gap.toFixed(1)} п.п.
				</div>
				<div style={{ fontSize: "13px", marginTop: "2px" }}>
					<span style={{ color: "var(--dark-fg-success, #1f9d57)", fontWeight: 600 }}>{best.pos_name}</span>
					<span className="text-muted"> ({Number(best.food_cost_percent).toFixed(1)}%) vs </span>
					<span style={{ color: "var(--dark-fg-danger, #d23434)", fontWeight: 600 }}>{worst.pos_name}</span>
					<span className="text-muted"> ({Number(worst.food_cost_percent).toFixed(1)}%)</span>
				</div>
				<div style={{ ...mutedStyle, marginTop: "8px" }}>
					Потенциал: если худшая подтянутся до уровня лучшего, экономия ~{formatMoney(Math.round(savings), 0)}
				</div>
			</div>
		</div>
	)
}

function FoodCost() {
	const reduxAccount = useSelector(state => state.account)
	const [data, setData] = useState(null)
	const [activeTab, setActiveTab] = useState("overview")

	const filters = useAnalyticsFilters({
		...buildPeriod("7days"),
		pos_id: reduxAccount.pos_id ?? -1,
		order_type_ids: [],
		source_ids: [],
		menu_ids: [],
		category_ids: [],
		target: 40,
	}, searchWithFilter)
	const { draft, patch } = filters
	const options = useAnalyticsOptions(["sources", "menus", "categories"], draft.pos_id)

	async function searchWithFilter(filter) {
		const response = await GET(buildAnalyticsUrl("/analytics/food-cost", filter), {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	const summary = data?.summary || {}
	const noCost = data?.no_cost || {}

	return (
		<>
			<AnalyticsFilterBar
				title="Food Cost"
				icon="uil-coins"
				subtitle="Анализ себестоимости и отклонений от нормы"
				filters={filters}
				tabs={tabs}
				activeTab={activeTab}
				onTabChange={setActiveTab}
			>
				<StandardFields filters={filters} options={options} />
				<FilterField label="Меню">
					<FilterSelect isMulti options={options.menus} allLabel="Все меню" value={draft.menu_ids} onChange={(ids) => patch({ menu_ids: ids })} />
				</FilterField>
				<FilterField label="Категория">
					<FilterSelect isMulti options={options.categories} allLabel="Все категории" value={draft.category_ids} onChange={(ids) => patch({ category_ids: ids })} />
				</FilterField>
				<FilterField label="Цель food cost, %">
					<input
						type="number"
						min="0"
						max="100"
						step="0.5"
						className="form-control"
						style={{ borderRadius: "10px", minHeight: "38px" }}
						value={draft.target}
						onChange={(event) => patch({ target: event.target.value === "" ? 0 : Number(event.target.value) })}
					/>
				</FilterField>
			</AnalyticsFilterBar>

			{noCost.products_count > 0 && (
				<div className="alert alert-warning d-flex align-items-center gap-2 py-2">
					<i className="uil uil-exclamation-triangle"></i>
					<span>
						<b>{formatMoney(noCost.products_count, 0)}</b> позиций без себестоимости на <b>{noCost.revenue_short}</b> выручки —
						они не входят в food cost. Заполните техкарты, иначе процент занижен.
					</span>
				</div>
			)}

			{activeTab === "overview" && (
				<>
					<div className="row g-3 mb-3">
						<div className="col-xl-3 col-md-6">
							<KpiCard
								title="Food Cost %"
								value={`${formatMoney(summary.food_cost_percent, 1)}%`}
								sublabel={`цель ${formatMoney(data?.target, 1)}%`}
								valueColor={Number(summary.food_cost_percent || 0) > Number(data?.target || 40) ? "#e0762c" : "#1f9d57"}
								icon="uil-percentage"
							/>
						</div>
						<div className="col-xl-3 col-md-6">
							<KpiCard title="Себестоимость" value={summary.cost_short || "0"} sublabel={`на выручку ${summary.revenue_short || "0"}`} icon="uil-invoice" />
						</div>
						<div className="col-xl-3 col-md-6">
							<KpiCard title="Потенциал экономии" value={summary.potential_savings_short || "0"} sublabel="если всё вернуть к норме" valueColor="#1f9d57" icon="uil-arrow-growth" />
						</div>
						<div className="col-xl-3 col-md-6">
							<KpiCard
								title="Блюд выше нормы"
								value={`${summary.dishes_above_norm || 0} / ${summary.dishes_total || 0}`}
								sublabel="блюд превышают цель"
								icon="uil-exclamation-triangle"
							/>
						</div>
					</div>
					<div style={{ ...mutedStyle, marginTop: "-8px" }} className="mb-3">
						<i className="uil uil-info-circle me-1"></i>{COST_HINT}. Выручка — с учётом скидки на чек, за вычетом возвратов.
					</div>

					<ProblemTable products={data?.problem_products || []} target={data?.target} />
					<EffectiveTable products={data?.effective_products || []} target={data?.target} />
				</>
			)}

			{activeTab === "branches" && (
				<>
					<BranchesTable branches={data?.branches || []} target={data?.target} />
					<BranchesChart branches={data?.branches || []} target={data?.target} />
					<BestWorstGap branches={data?.branches || []} />
				</>
			)}
		</>
	)
}

export default FoodCost
